import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  X, 
  Check,
  Phone,
  Mail,
  Calendar,
  FileText,
  Users,
  Clock,
  Brain,
  TrendingUp,
  Shield,
  Zap,
  Target,
  HeartHandshake,
  Receipt
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const CALENDLY_URL = "https://calendly.com/romain-trallero/diagnostic-transition-digitale-60min";

const problemSolutions = [
  {
    problem: {
      icon: Phone,
      title: "Tu rates des appels sur tes chantiers",
      description: "Chaque appel manqué est potentiellement un client perdu. Tu es sur le terrain, les mains dans le travail, et ton téléphone sonne dans le vide."
    },
    solution: {
      icon: Phone,
      title: "Standard téléphonique IA 24h/24",
      description: "Un agent IA répond à tes appels, qualifie les demandes, prend les rendez-vous et t'envoie un résumé. Tu ne perds plus jamais un prospect."
    }
  },
  {
    problem: {
      icon: Mail,
      title: "Ta boîte mail déborde",
      description: "Des dizaines d'emails non lus s'accumulent. Demandes clients, fournisseurs, administratif... Tu ne sais plus par où commencer."
    },
    solution: {
      icon: Mail,
      title: "Gestion intelligente des emails",
      description: "Ton assistant trie, répond aux demandes simples, signale les urgences et crée des tâches. Ta boîte mail redevient un outil, pas un cauchemar."
    }
  },
  {
    problem: {
      icon: Calendar,
      title: "Tu oublies des rendez-vous",
      description: "Entre les chantiers, les devis à faire et les relances, difficile de tout retenir. Un oubli peut coûter cher en crédibilité."
    },
    solution: {
      icon: Calendar,
      title: "Organisation et rappels automatiques",
      description: "Calendrier synchronisé, rappels SMS, préparation des dossiers avant chaque RDV. Tu arrives toujours préparé et à l'heure."
    }
  },
  {
    problem: {
      icon: FileText,
      title: "Tes devis prennent trop de temps",
      description: "Le soir, après une journée de travail physique, tu dois encore passer des heures à faire des devis. La fatigue s'accumule."
    },
    solution: {
      icon: FileText,
      title: "Devis générés en quelques minutes",
      description: "Dicte les informations, notre système génère le devis. Ton assistant vérifie et envoie. Tu récupères tes soirées."
    }
  },
  {
    problem: {
      icon: Users,
      title: "Pas de temps pour prospecter",
      description: "Tu sais qu'il faut développer ton réseau, mais entre le travail et l'administratif, il ne reste plus de temps pour trouver de nouveaux clients."
    },
    solution: {
      icon: Users,
      title: "Prospection automatisée",
      description: "Agents IA qui prospectent sur LinkedIn, réseaux sociaux et par email. Ton carnet de commandes se remplit sans effort de ta part."
    }
  },
  {
    problem: {
      icon: Receipt,
      title: "Trésorerie tendue, paiements en retard",
      description: "Tu n'as pas le temps de relancer tes clients. Les factures s'accumulent, les paiements tardent, et ta trésorerie en souffre."
    },
    solution: {
      icon: Receipt,
      title: "Relances automatiques, trésorerie sous contrôle",
      description: "Ton assistant suit les paiements et relance automatiquement les clients en retard. Ta trésorerie reste saine sans effort."
    }
  },
  {
    problem: {
      icon: Clock,
      title: "Tu travailles 70h par semaine",
      description: "Le travail sur chantier + l'administratif + la gestion = des semaines interminables. Ta vie personnelle en pâtit."
    },
    solution: {
      icon: Clock,
      title: "Récupère 20h par semaine",
      description: "En déléguant l'administratif et la gestion à ton Super Assistant, tu retrouves du temps pour toi, ta famille, ou développer ton activité."
    }
  },
];

const keyBenefits = [
  { icon: Brain, title: "Moins de charge mentale", description: "Arrête de tout retenir dans ta tête" },
  { icon: TrendingUp, title: "Plus de chiffre d'affaires", description: "Plus de prospects, moins de clients perdus" },
  { icon: Shield, title: "Zéro risque", description: "Supervision humaine de toutes les actions IA" },
  { icon: HeartHandshake, title: "Un vrai partenaire", description: "Une équipe dédiée qui comprend ton métier" },
];

export default function NosSolutionsPage() {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-ba-secondary/10 text-ba-secondary text-xs font-semibold uppercase tracking-wider rounded-full mb-6">
              <Target className="w-4 h-4" />
              Nos Solutions
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-slate-800 mb-6 leading-tight">
              Transforme tes obstacles{' '}
              <span className="text-ba-secondary">en tremplin</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto mb-10 leading-relaxed">
              Tu te reconnais dans ces situations ? Découvre comment <strong className="text-slate-800">BatiAssist</strong> transforme tes galères quotidiennes en organisation fluide.
            </p>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
              <Button className="bg-ba-secondary hover:bg-ba-secondary/90 text-white font-semibold rounded-full px-8 py-6 text-lg shadow-lg">
                Parler de mes problemes
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="space-y-8 md:space-y-12">
            {problemSolutions.map((item, index) => {
              const ProblemIcon = item.problem.icon;
              const SolutionIcon = item.solution.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="grid md:grid-cols-2 gap-4 md:gap-6"
                >
                  {/* Problem Card */}
                  <div className="relative bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl md:rounded-3xl p-6 md:p-8 border border-red-100 overflow-hidden group hover:shadow-lg transition-shadow">
                    <div className="absolute top-4 right-4 w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                      <X className="w-4 h-4 text-red-500" />
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                        <ProblemIcon className="w-7 h-7 text-red-500" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs font-semibold text-red-500 uppercase tracking-wider mb-2">Le probleme</p>
                        <h3 className="text-xl md:text-2xl font-bold text-slate-800 mb-3">
                          {item.problem.title}
                        </h3>
                        <p className="text-slate-600 leading-relaxed">
                          {item.problem.description}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Solution Card */}
                  <div className="relative bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl md:rounded-3xl p-6 md:p-8 border border-green-100 overflow-hidden group hover:shadow-lg transition-shadow">
                    <div className="absolute top-4 right-4 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-green-500" />
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 bg-green-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                        <SolutionIcon className="w-7 h-7 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs font-semibold text-green-600 uppercase tracking-wider mb-2">Notre solution</p>
                        <h3 className="text-xl md:text-2xl font-bold text-slate-800 mb-3">
                          {item.solution.title}
                        </h3>
                        <p className="text-slate-600 leading-relaxed">
                          {item.solution.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Arrow connector for visual flow - Desktop only */}
      <section className="py-12 bg-slate-50 hidden md:block">
        <div className="container mx-auto px-4 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-4 bg-white rounded-full px-8 py-4 shadow-lg border border-slate-100">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                <span className="text-slate-600 font-medium">Vos problemes</span>
              </div>
              <div className="flex items-center">
                <div className="w-12 h-0.5 bg-gradient-to-r from-red-300 via-slate-300 to-green-300"></div>
                <Zap className="w-6 h-6 text-ba-secondary mx-2" />
                <div className="w-12 h-0.5 bg-gradient-to-r from-slate-300 to-green-300"></div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                <span className="text-slate-600 font-medium">Nos solutions</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
              Ce que tu gagnes avec BatiAssist
            </h2>
            <p className="text-lg text-slate-600">
              Au-dela des solutions, de vrais benefices pour ton quotidien
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {keyBenefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center p-6 bg-slate-50 rounded-2xl hover:bg-slate-100 transition-colors"
                >
                  <div className="w-14 h-14 bg-ba-secondary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-7 h-7 text-ba-secondary" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-800 mb-2">{benefit.title}</h3>
                  <p className="text-slate-600 text-sm">{benefit.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-ba-secondary to-orange-500">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              Prêt à transformer tes problèmes en solutions ?
            </h2>
            <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
              Réserve un entretien gratuit avec Romain. Ensemble, nous analyserons tes besoins et te montrerons comment BatiAssist peut t'aider.
            </p>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
              <Button className="bg-white hover:bg-white/90 text-ba-secondary font-semibold rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all group">
                Reserver mon entretien gratuit
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
